/**
 * Finding a scenario type two ways at once. Typing filters the list on screen instantly;
 * past three characters a debounced account-wide search joins in, which also names types
 * under other SOPs. The two are independent because the account-wide index is not
 * configured everywhere, and typing must still filter without it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchesAllTerms, searchTerms } from '@/lib/search';
import { backTestingUrl } from '@/constants/paths';
import { aiFunctionService, type ScenarioTypeMatch, type ScenarioTypeSearchResult } from '@/services/ai-function.service';
import type { ScenarioType } from '../types';

/** The most rows the dropdown offers, this SOP's matches first. */
const MAX_ROWS = 8;
/** Below this the account-wide query is noise, and every keystroke would spend one. */
const MIN_QUERY = 3;
const DEBOUNCE_MS = 300;

interface SemanticRow {
	match: ScenarioTypeMatch;
	/** Under another SOP, so activating it leaves this page. */
	external: boolean;
}

export const useTypeSearch = (sopId: string, types: ScenarioType[], onSelectType: (typeId: string) => void) => {
	const [search, setSearch] = useState('');
	const [result, setResult] = useState<ScenarioTypeSearchResult | null>(null);
	const [unavailable, setUnavailable] = useState(false);
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const anchorRef = useRef<HTMLDivElement>(null);

	const terms = useMemo(() => searchTerms(search), [search]);
	const visibleTypes = useMemo(
		() =>
			terms.length
				? types.filter((type) => matchesAllTerms(`${type.description} ${type.outcome}`.toLowerCase(), terms))
				: types,
		[types, terms]
	);

	useEffect(() => {
		const query = search.trim();
		if (query.length < MIN_QUERY) {
			setResult(null);
			setOpen(false);
			setLoading(false);
			setUnavailable(false);
			return;
		}
		setLoading(true);
		let stale = false;
		const timer = setTimeout(async () => {
			try {
				const found = await aiFunctionService.searchTypes(sopId, query);
				if (stale) return;
				setResult(found);
				setUnavailable(false);
				setActiveIndex(-1);
				setOpen(true);
			} catch (err) {
				if (stale) return;
				setResult(null);
				// 503: the index is not configured on this environment, a deploy-time state
				// rather than a blip, so say so instead of silently never opening.
				if ((err as { response?: { status?: number } })?.response?.status === 503) {
					setUnavailable(true);
					setOpen(true);
				}
			} finally {
				if (!stale) setLoading(false);
			}
		}, DEBOUNCE_MS);
		return () => {
			stale = true;
			clearTimeout(timer);
		};
	}, [search, sopId]);

	// This SOP's matches first; the rest of the budget goes to the others.
	const sameSopMatches = useMemo(() => result?.sameSop.slice(0, MAX_ROWS) ?? [], [result]);
	const otherSopMatches = useMemo(
		() => result?.otherSops.slice(0, Math.max(0, MAX_ROWS - (result?.sameSop.length ?? 0))) ?? [],
		[result]
	);
	// One flat list under the two sections, so the arrow keys walk all of it.
	const rows = useMemo<SemanticRow[]>(
		() => [
			...sameSopMatches.map((match) => ({ match, external: false })),
			...otherSopMatches.map((match) => ({ match, external: true }))
		],
		[sameSopMatches, otherSopMatches]
	);

	const activate = useCallback(
		(row: SemanticRow) => {
			if (row.external) {
				// Another SOP's type opens on its own page, preselected.
				window.open(backTestingUrl(row.match.sopId, row.match.typeId), '_blank');
				return;
			}
			onSelectType(row.match.typeId);
			setOpen(false);
		},
		[onSelectType]
	);

	const onSearchKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (!open || !rows.length) return;
			if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
				event.preventDefault();
				const delta = event.key === 'ArrowDown' ? 1 : -1;
				setActiveIndex((prev) => (prev + delta + rows.length) % rows.length);
			} else if (event.key === 'Enter') {
				const row = rows[activeIndex];
				if (!row) return;
				event.preventDefault();
				activate(row);
			}
		},
		[open, rows, activeIndex, activate]
	);

	/** Exactly the shape the list's popover takes. */
	const semantic = useMemo(
		() => ({
			open: open && (result !== null || unavailable),
			onOpenChange: (next: boolean) => {
				setOpen(next);
				if (!next) setActiveIndex(-1);
			},
			anchorRef,
			loading,
			unavailable,
			rows,
			sameSopMatches,
			otherSopMatches,
			activeIndex,
			onActivate: activate,
			onFocus: () => (result || unavailable) && setOpen(true)
		}),
		[open, result, unavailable, loading, rows, sameSopMatches, otherSopMatches, activeIndex, activate]
	);

	return { search, setSearch, terms, visibleTypes, semantic, onSearchKeyDown };
};
